import { FC, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Checkbox, Loading } from 'react-daisyui';
import { CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import type {
  RecommendationEngineData,
  RecommendationEngineSteps,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';
import Routes from '@/router/Routes.ts';
import { cn } from '@/utils/classNames.ts';

const StepSessionRecorded: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contextData, setContextData, goToStep } = useStepperContext<
    RecommendationEngineSteps,
    RecommendationEngineData
  >();
  const d = contextData as Partial<RecommendationEngineData> | null;
  const [saveLanguage, setSaveLanguage] = useState(d?.save_session_language ?? true);
  const [commitError] = useState<string | null>(null);

  const isEscalatedPath = d?.session_outcome === 'escalated';
  const isLedgerSuccess = d?.session_outcome === 'ledger_success';
  const isLedgerError = d?.session_outcome === 'ledger_error';

  const frameworkLabel =
    d?.ledger_commit?.framework_label ??
    (d?.framework_attribution ||
      FRAMEWORK_BY_ID[d?.governance_preview?.framework_id as FrameworkId]?.label ||
      d?.governance_preview?.framework_id ||
      '—');

  const governanceScoreDisplay =
    d?.ledger_commit?.governance_score ?? d?.governance_result?.governance_score;

  const sessionIndex = d?.ledger_commit?.session_index;
  const ledgerHash = d?.ledger_commit?.ledger_hash;
  const sealedAt = d?.ledger_commit?.sealed_at;

  useEffect(() => {
    if (isEscalatedPath || isLedgerSuccess || isLedgerError) return;
    setContextData((prev) => ({ ...prev, session_outcome: 'ledger_error' }));
  }, [isEscalatedPath, isLedgerSuccess, isLedgerError, setContextData]);

  const showLoading =
    !isEscalatedPath &&
    !isLedgerSuccess &&
    !isLedgerError &&
    d?.session_outcome === 'pending';

  return (
    <BrandCardSideContent centerContent>
      <div className="flex flex-col flex-1 gap-6 w-full max-w-lg mx-auto">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('recommendationEnginePage.session.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.session.subtitle')}</StepFormSubtitle>
        </div>

        {showLoading && (
          <div className="flex flex-col items-center justify-center gap-4 py-10">
            <Loading size="lg" className="text-[#0F1B2A]" />
            <p className="font-dm text-sm font-semibold text-black/70 text-center">
              {t('recommendationEnginePage.session.sealing')}
            </p>
          </div>
        )}

        {!showLoading && isEscalatedPath && (
          <>
            <Alert status="success" className="rounded-xl bg-[#E8F5E9] border-[#C8E6C9] text-[#2E7D32]">
              <CheckCircleIcon className="w-5 h-5 shrink-0" />
              <span className="font-dm text-sm font-semibold">
                {t('recommendationEnginePage.session.escalatedBanner')}
              </span>
            </Alert>

            <SessionDemonstratedBox
              frameworkLabel={frameworkLabel}
              governanceScore={governanceScoreDisplay}
              sessionIndex={sessionIndex}
              escalated
            />

            <p className="font-dm text-xs italic text-black/50 text-center">
              {t('recommendationEnginePage.session.revisitHint')}
            </p>

            <SessionActions onDashboard={() => navigate(Routes.DASHBOARD)} />
          </>
        )}

        {!showLoading && isLedgerSuccess && (
          <>
            <Alert status="success" className="rounded-xl bg-[#E8F5E9] border-[#C8E6C9] text-[#2E7D32]">
              <CheckCircleIcon className="w-5 h-5 shrink-0" />
              <span className="font-dm text-sm font-semibold">
                {t('recommendationEnginePage.session.ledgerBanner')}
              </span>
            </Alert>

            <SessionDemonstratedBox
              frameworkLabel={frameworkLabel}
              governanceScore={governanceScoreDisplay}
              sessionIndex={sessionIndex}
              ledgerHash={ledgerHash}
              sealedAt={sealedAt}
            />

            <p className="font-dm text-xs italic text-black/50">
              {t('recommendationEnginePage.session.revisitHint')}
            </p>

            <label className="flex flex-row items-center gap-2 cursor-pointer font-dm text-sm text-black/80">
              <Checkbox checked={saveLanguage} onChange={(e) => setSaveLanguage(e.target.checked)} color="primary" />
              {t('recommendationEnginePage.session.saveLanguage')}
            </label>

            <SessionActions onDashboard={() => navigate(Routes.DASHBOARD)} />
          </>
        )}

        {!showLoading && isLedgerError && (
          <>
            <Alert status="error" className="rounded-xl">
              <span className="font-dm text-sm">
                {commitError ?? t('recommendationEnginePage.session.generationFailed')}
              </span>
            </Alert>

            <p className="font-dm text-sm text-black/70 text-center">
              {t('recommendationEnginePage.session.subtitle')}
            </p>

            <LoadingButton
              loading={false}
              color="primary"
              className="w-full"
              type="button"
              onClick={() => {
                setContextData((prev) => ({
                  ...prev,
                  session_outcome: undefined,
                  governance_sealed: false,
                }));
                goToStep('compass-review');
              }}
            >
              {t('recommendationEnginePage.session.tryAgain')}
            </LoadingButton>

            <button
              type="button"
              className="w-full py-2 text-sm font-dm font-bold text-[#0F1B2A] opacity-70"
              onClick={() => navigate(Routes.DASHBOARD)}
            >
              {t('recommendationEnginePage.session.returnDashboard')}
            </button>
          </>
        )}
      </div>
    </BrandCardSideContent>
  );
};

export default StepSessionRecorded;

function truncateHash(hash: string): string {
  if (hash.length <= 16) return hash;
  return `${hash.slice(0, 8)}…${hash.slice(-8)}`;
}

function SessionDemonstratedBox({
  frameworkLabel,
  governanceScore,
  sessionIndex,
  ledgerHash,
  sealedAt,
  escalated = false,
}: {
  frameworkLabel: string;
  governanceScore?: number | null;
  sessionIndex?: number;
  ledgerHash?: string;
  sealedAt?: string;
  escalated?: boolean;
}) {
  const { t } = useTranslation();
  const scoreText =
    governanceScore != null ? (governanceScore * 10).toFixed(1) : '—';

  return (
    <div className="rounded-xl border border-[rgba(0,0,0,0.12)] p-4 flex flex-col gap-3">
      <div className="flex flex-row items-start gap-2">
        <InformationCircleIcon className="w-5 h-5 shrink-0 text-[#1A5292]" aria-hidden />
        <p className="font-dm text-sm font-semibold text-black/85">
          {t('recommendationEnginePage.session.demonstratedTitle')}
        </p>
      </div>
      <ul className="list-disc pl-6 font-dm text-sm text-black/75 space-y-1">
        <li>{t('recommendationEnginePage.session.frameworkApplied', { framework: frameworkLabel })}</li>
        {!escalated && (
          <li>{t('recommendationEnginePage.session.governanceScore', { score: scoreText })}</li>
        )}
        {escalated && (
          <li>{t('recommendationEnginePage.session.markedForReview')}</li>
        )}
        {sessionIndex != null && (
          <li>
            {t('recommendationEnginePage.session.sessionIndex', { index: sessionIndex })}
          </li>
        )}
        {ledgerHash && (
          <li>
            {t('recommendationEnginePage.session.ledgerHash', { hash: truncateHash(ledgerHash) })}
          </li>
        )}
        {sealedAt && (
          <li>
            {t('recommendationEnginePage.session.sealedAt', {
              date: new Date(sealedAt).toLocaleString(),
            })}
          </li>
        )}
      </ul>
    </div>
  );
}

function SessionActions({ onDashboard }: { onDashboard: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-2 mt-auto">
      <LoadingButton loading={false} color="primary" className="w-full" type="button" onClick={onDashboard}>
        {t('recommendationEnginePage.session.returnDashboard')}
      </LoadingButton>
      <Link
        to={Routes.SESSION_ARCHIVE}
        className={cn(
          'w-full py-3 px-4 rounded-xl font-dm font-bold text-sm text-center',
          'border border-[rgba(0,0,0,0.12)] bg-white text-[#0F1B2A]',
        )}
      >
        {t('recommendationEnginePage.session.viewArchive')}
      </Link>
      <p className="text-center font-dm text-sm text-black/60 pt-2 flex flex-col items-center">
        {t('recommendationEnginePage.session.orStartNew')}{' '}
        <Link to={Routes.COMMON_INTAKE} className="font-bold text-[#0F1B2A] underline">
          {t('recommendationEnginePage.session.startNew')}
        </Link>
      </p>
    </div>
  );
}
